import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { ShieldAlert, AlertTriangle, Megaphone, PenLine, FileSignature, Loader2 } from 'lucide-react';
import PushSetupCard from './PushSetupCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY = {
  critical: { label: 'CRITICAL', badge: 'bg-red-600 text-white', ring: 'border-red-500', Icon: ShieldAlert },
  important: { label: 'IMPORTANT', badge: 'bg-amber-500 text-slate-900', ring: 'border-amber-500', Icon: AlertTriangle },
  normal: { label: 'ANNOUNCEMENT', badge: 'bg-blue-600 text-white', ring: 'border-blue-500', Icon: Megaphone },
};

// Full-screen, non-dismissible gate that blocks the app until every pending
// mandatory company announcement / waiver has been signed off by the current user.
export default function AnnouncementGate() {
  const [pending, setPending] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  // Waiver-specific state
  const [waiverMeta, setWaiverMeta] = useState(null);
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const sigRef = useRef(null);

  const token = localStorage.getItem('token');

  const load = useCallback(async () => {
    try {
      if (!token) { setLoaded(true); return; }
      const r = await axios.get(`${API}/announcements/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPending(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setPending([]);
    } finally {
      setLoaded(true);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const open = loaded && pending.length > 0;
  const current = open ? pending[0] : null;
  const isWaiver = current?.type === 'waiver';

  // Reset per-item state and load waiver page count when the current item changes
  useEffect(() => {
    setHasInk(false);
    setAgreed(false);
    setWaiverMeta(null);
    setScrolledEnd(false);
    if (current && current.type === 'waiver' && current.waiver_template_id) {
      if (!current.require_scroll) setScrolledEnd(true);
      axios.get(`${API}/waivers/templates/${current.waiver_template_id}/meta`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => setWaiverMeta(r.data)).catch(() => setWaiverMeta({ page_count: 1 }));
    }
  }, [current?.id]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  if (!open) return null;

  const meta = PRIORITY[current.priority] || PRIORITY.normal;
  const Icon = isWaiver ? FileSignature : meta.Icon;
  const total = pending.length;

  const clearSig = () => { sigRef.current?.clear(); setHasInk(false); };

  const onDocScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) setScrolledEnd(true);
  };

  const canSign = !isWaiver || ((!current.require_scroll || scrolledEnd) && (!current.require_agree || agreed));

  const acknowledge = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) return;
    setSubmitting(true);
    try {
      const signature = sigRef.current.toDataURL('image/png');
      if (isWaiver) {
        await axios.post(
          `${API}/waivers/${current.id}/sign`,
          { signature_data: signature },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/announcements/${current.id}/acknowledge`,
          { method: 'signature', signature_data: signature },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      clearSig();
      setPending(prev => prev.filter(a => a.id !== current.id));
    } catch (e) {
      alert('Could not record your signature. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const pageCount = waiverMeta?.page_count || 0;

  return (
    <div
      data-testid="announcement-gate"
      className="fixed inset-0 z-[99999] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
    >
      <div className={`w-full max-w-2xl bg-white border-2 ${meta.ring} rounded-2xl shadow-2xl my-auto`}>
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-slate-200">
          <div className="flex items-center justify-between gap-3">
            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold tracking-wide ${isWaiver ? 'bg-amber-500 text-slate-900' : meta.badge}`}>
              <Icon className="w-4 h-4" /> {isWaiver ? 'WAIVER — SIGNATURE REQUIRED' : meta.label}
            </span>
            <span data-testid="announcement-gate-progress" className="text-xs font-medium text-slate-500">
              {total > 1 ? `1 of ${total} to sign` : 'Action required'}
            </span>
          </div>
          <h2 data-testid="announcement-gate-title" className="mt-4 text-2xl font-bold text-slate-900 leading-tight">
            {current.title}
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Posted by {current.created_by} · {new Date(current.created_at).toLocaleDateString('en-US', { timeZone: 'America/Toronto' })}
          </p>
        </div>

        {/* Message / instructions */}
        {current.message ? (
          <div className={`p-5 sm:p-6 ${isWaiver ? 'pb-2' : 'max-h-[34vh] overflow-y-auto'}`}>
            <p data-testid="announcement-gate-message" className="text-sm sm:text-base text-slate-800 whitespace-pre-wrap leading-relaxed">
              {current.message}
            </p>
          </div>
        ) : null}

        {/* Optional action: inline push-notification setup, or a link button */}
        {current.action === 'enable_push' && (
          <div className="px-5 sm:px-6 pb-2" data-testid="announcement-gate-push-action">
            <PushSetupCard compact />
          </div>
        )}
        {current.action === 'link' && current.action_url && (
          <div className="px-5 sm:px-6 pb-2">
            <a href={current.action_url} target={/^https?:/i.test(current.action_url) ? '_blank' : undefined} rel="noreferrer"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold px-5 py-3 rounded-xl text-sm"
              data-testid="announcement-gate-link-action">
              {current.action_label || 'Open'} →
            </a>
          </div>
        )}

        {/* Waiver document viewer */}
        {isWaiver && (
          <div className="px-5 sm:px-6">
            <div
              data-testid="waiver-doc-scroll"
              onScroll={onDocScroll}
              className="bg-white rounded-xl border border-slate-200 overflow-y-auto"
              style={{ maxHeight: '42vh' }}
            >
              {pageCount === 0 ? (
                <div className="flex items-center justify-center py-16 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading document…
                </div>
              ) : (
                Array.from({ length: pageCount }).map((_, i) => (
                  <img
                    key={i}
                    src={`${API}/waivers/templates/${current.waiver_template_id}/page/${i}?token=${encodeURIComponent(token)}&autofill=1`}
                    alt={`Waiver page ${i + 1}`}
                    className="w-full block"
                    loading="eager"
                  />
                ))
              )}
            </div>
            {current.require_scroll && !scrolledEnd && (
              <p className="mt-2 text-center text-xs text-amber-700" data-testid="waiver-scroll-hint">
                ↓ Scroll to the end of the document to continue
              </p>
            )}
          </div>
        )}

        {/* Agree checkbox (waiver) */}
        {isWaiver && current.require_agree && (
          <div className="px-5 sm:px-6 pt-4">
            <label className={`flex items-start gap-3 text-sm ${scrolledEnd ? 'text-slate-800 cursor-pointer' : 'text-slate-500 cursor-not-allowed'}`}>
              <input
                type="checkbox"
                data-testid="waiver-agree-checkbox"
                disabled={!scrolledEnd}
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                className="mt-0.5 w-5 h-5 rounded shrink-0"
              />
              <span>I have read and understand this waiver, and I agree to be bound by its terms.</span>
            </label>
          </div>
        )}

        {/* Signature */}
        <div className="p-5 sm:p-6 border-t border-slate-200 mt-2">
          <div className="flex items-center gap-2 mb-2">
            <PenLine className="w-4 h-4 text-slate-500" />
            <p className="text-sm font-semibold text-slate-900">
              {isWaiver ? 'Sign below with your finger to accept this waiver' : "Sign below to acknowledge you've read this"}
            </p>
          </div>
          <div className={`relative bg-white rounded-xl overflow-hidden border border-slate-300 ${canSign ? '' : 'opacity-50'}`} data-testid="announcement-gate-sign-canvas">
            <SignatureCanvas
              ref={sigRef}
              penColor="black"
              backgroundColor="white"
              onEnd={() => setHasInk(true)}
              canvasProps={{ className: 'w-full', style: { width: '100%', height: '180px' } }}
            />
            {!canSign && (
              <div className="absolute inset-0 bg-white/40 cursor-not-allowed" title="Read the document and tick the box to sign" />
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <button
              data-testid="announcement-gate-clear-btn"
              onClick={clearSig}
              disabled={submitting}
              className="px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Clear
            </button>
            <button
              data-testid="announcement-gate-acknowledge-btn"
              onClick={acknowledge}
              disabled={!hasInk || !canSign || submitting}
              className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? 'Saving…' : (isWaiver ? 'Sign & Accept Waiver' : 'Sign & Acknowledge')}
            </button>
          </div>
          <p className="mt-3 text-center text-xs text-slate-500">
            {isWaiver
              ? 'Your signature is added to the waiver and filed in your employee records. Your name, signature and time are recorded.'
              : 'You must sign off before you can use the app. Your name, signature and time are recorded.'}
          </p>
        </div>
      </div>
    </div>
  );
}
